import React, { useState } from 'react';
import { Modal, Card, Row, Col, Form, Input, Select, Checkbox, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchData } from '../../service/dataServices';
import { getLocalizedTitle } from '../../utils/localization';
import TranslatableInput from '../../components/form/TranslatableInput';

const { Option } = Select;

function getColWidth(width) {
  if (width === '1') return 24;
  if (width === '1/2') return 12;
  if (width === '1/3') return 8;
  if (width === '1/4') return 6;
  return 24;
}

// Fillable preview: real inputs and real dropdown/user options so it behaves
// like the actual form, but this modal never calls a save endpoint - there is
// no submit button and no onFinish handler, so nothing entered here persists.
function PreviewField({ field, t, translated, getDropdownOptions, users }) {
  if (!field) return null;
  const label = translated(field.name);

  switch (field.type) {
    case 'long_text':
      return (
        <Form.Item name={field.id} label={label}>
          <TranslatableInput 
            textarea={true} 
            placeholder={{ en: 'Enter long text', ar: 'أدخل نصاً طويلاً' }}
          />
        </Form.Item>
      );
    case 'number':
      return <Form.Item name={field.id} label={label}><Input type="number" /></Form.Item>;
    case 'boolean':
      return <Form.Item name={field.id} valuePropName="checked"><Checkbox>{label}</Checkbox></Form.Item>;
    case 'dropdown':
      return (
        <Form.Item name={field.id} label={label}>
          <Select placeholder={t('customForm.selectOptionPlaceholder', 'Select an option')} allowClear options={getDropdownOptions(field)} />
        </Form.Item>
      );
    case 'user_dropdown':
      return (
        <Form.Item name={field.id} label={label}>
          <Select showSearch filterOption optionFilterProp="children" placeholder={t('common.select_user', 'Select a user')} allowClear>
            {users.map(user => <Option value={user.id} key={user.id}>{user.name}</Option>)}
          </Select>
        </Form.Item>
      );
    case 'date':
      return <Form.Item name={field.id} label={label}><DatePicker style={{ width: '100%' }} /></Form.Item>;
    case 'single_photo':
    case 'multiple_photos':
    case 'pdf_upload':
      return (
        <Form.Item label={label}>
          <div style={{
            border: '1px dashed #d9d9d9',
            borderRadius: 6,
            padding: 16,
            textAlign: 'center',
            color: '#bfbfbf',
          }}
          >
            {label}
          </div>
        </Form.Item>
      );
    default:
      return <Form.Item name={field.id} label={label}><Input /></Form.Item>;
  }
}

export default function FormPreviewModal({ open, onClose, formName, sections }) {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const translated = (val) => (val && (val[i18n.language] || val.en)) || '';

  const [allDropdowns, setAllDropdowns] = useState({});
  const [dropdownIdMap, setDropdownIdMap] = useState({});
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);
  const [objectFilters, setObjectFilters] = useState({});

  fetchData('/api/dropdowns', setAllDropdowns);
  fetchData('/api/dropdowns/types', (types) => {
    const idMap = {};
    types?.forEach(type => { idMap[type.id] = type.title; });
    setDropdownIdMap(idMap);
  });
  fetchData('/api/users/user-refs', setUsers);
  const objectResponse = fetchData('/api/objects', (data) => setObjects(data?.content || []), objectFilters, open);

  const getDropdownOptions = (field) => {
    const res = allDropdowns?.[dropdownIdMap?.[field.dropdownType]];
    if (!res) return [];
    return res.map(r => ({ value: r.id, label: getLocalizedTitle(r) }));
  };

  const onObjectSearch = (val) => {
    setObjectFilters((prev) => ({ ...prev, inventoryNumber: val }));
  };

  return (
    <Modal
      title={`${t('customForm.preview', 'Preview')}${formName ? `: ${translated(formName)}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="objectId" label={t('customForm.selectObject')}>
              <Select
                showSearch
                placeholder={t('customForm.selectObjectPlaceholder')}
                allowClear
                onSearch={onObjectSearch}
                filterOption={false}
                loading={objectResponse?.isFetching}
              >
                {objects.map(option => (
                  <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {(sections || []).map(section => (
          <Card key={section.id} title={translated(section.title)} style={{ marginBottom: 20 }}>
            {(section.rows || []).map(row => (
              <Row key={row.id} gutter={16}>
                {(row.cols || []).map(col => (
                  <Col key={col.id} span={getColWidth(col.width)}>
                    <PreviewField field={col.field} t={t} translated={translated} getDropdownOptions={getDropdownOptions} users={users} />
                  </Col>
                ))}
              </Row>
            ))}
          </Card>
        ))}
      </Form>
    </Modal>
  );
}
